/**
 * Funnel session lifecycle helpers.
 *
 * `resolveFunnelConfig` was removed — the combined `/api/init` endpoint
 * (see `apps/funnel-api/src/routes/init.ts`) now returns both the
 * application context and funnel config in a single obfuscated payload.
 * Session creation remains a separate POST for HTTP-safety reasons.
 */

export type CreateSessionArgs = {
  applicationId: string;
  anonymousId: string;
  funnelId: string;
  overrideId: string | null;
  attribution?: Record<string, unknown>;
  experimentId?: string;
  /** NX-9949: Experiment arm id assigned via bucketing. */
  armId?: string;
  /** NX-9847: Published funnel version number. */
  funnelVersion?: number;
  /** NX-10213: Mark the session as preview so analytics are suppressed. */
  isPreview?: boolean;
  fetchImpl?: typeof fetch;
  signal?: AbortSignal;
};

export type CreateSessionResult = {
  session_id: string;
  started_at: number;
};

const MAX_RETRIES = 2;
const INITIAL_DELAY_MS = 500;

const isRetryableStatus = (status: number): boolean => status >= 500 && status < 600;

const sleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

export const createSession = async (args: CreateSessionArgs): Promise<CreateSessionResult> => {
  const { fetchImpl = fetch, signal } = args;
  const body = JSON.stringify({
    application_id: args.applicationId,
    anonymous_id: args.anonymousId,
    funnel_id: args.funnelId,
    ...(args.overrideId !== null ? { override_id: args.overrideId } : {}),
    attribution: args.attribution ?? {},
    ...(args.experimentId ? { experiment_id: args.experimentId } : {}),
    ...(args.armId ? { arm_id: args.armId } : {}),
    ...(args.funnelVersion !== undefined ? { funnel_version: args.funnelVersion } : {}),
    ...(args.isPreview ? { is_preview: true } : {}),
  });

  let lastStatus = 0;
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    if (attempt > 0) await sleep(INITIAL_DELAY_MS * 2 ** (attempt - 1));
    const response = await fetchImpl("/api/funnel/sessions", {
      method: "POST",
      headers: { "content-type": "application/json" },
      credentials: "same-origin",
      body,
      ...(signal ? { signal } : {}),
    });
    if (response.ok) {
      return (await response.json()) as CreateSessionResult;
    }
    lastStatus = response.status;
    if (!isRetryableStatus(response.status)) break;
  }
  throw new Error(`session_create_failed:${lastStatus}`);
};
